import React, { useMemo } from "react";
import { Tooltip } from "antd";
import { getLeadAvailableRootOppTypeIds, getRootOppTypeId } from "../../../library/oppTypeScoping";
import { getRootConfig, ROOT_OPP_TYPES } from "../../../library/oppTypeRootConfig";

/**
 * Render one small icon per root opp_type the lead has. Known roots
 * from ``ROOT_OPP_TYPES`` (Sales, Service) render first in their
 * config order; unknown roots (tenant-specific, e.g. AutoSavvy
 * Customer Service) render after, in ascending id order, using the
 * fallback config from ``getRootConfig`` with the BE-supplied
 * opp_type name as both the tooltip and the abbreviation source.
 *
 * The ``highlightedRootId`` gets a tinted background chip; the
 * others render as bare icons. Tooltip on every icon shows the
 * root's full display name (e.g. "AutoSavvy Customer Service").
 *
 * @param {object} lead — lead row with ``leadOpportunities``.
 * @param {number|null} highlightedRootId — root id to highlight.
 * @param {number} size — icon glyph size in px (default 13). The
 *   highlighted chip scales with it.
 * @param {function} tooltipFormatter — maps a root's display name to the
 *   tooltip text (default: the name itself). Lets callers say e.g.
 *   "Sales opportunity" without changing the label everywhere.
 */
const LeadOppTypeIcons = ({
  lead,
  highlightedRootId,
  size = 13,
  tooltipFormatter = name => name,
}) => {
  // Chip is sized relative to the glyph so it keeps the same padding at
  // any icon size (20px chip around a 13px glyph in the original design).
  const chipSize = size + 7;
  const presentRoots = useMemo(() => getLeadAvailableRootOppTypeIds(lead), [lead]);

  // Build a root id → opp_type name map from the lead's own opps so
  // unknown roots get a meaningful tooltip / abbreviation even when
  // ``activeRootOppTypes`` isn't plumbed through to the lead-list
  // payload (it isn't today — that field lives on the lead-detail
  // location response). Cap chain walk at 5 hops as a safety net.
  const rootNameById = useMemo(() => {
    const map = new Map();
    (lead?.leadOpportunities || []).forEach(opp => {
      const ot = opp?.opportunityType;
      if (!ot) return;
      const rootId = getRootOppTypeId(ot);
      if (!rootId || map.has(Number(rootId))) return;
      let cursor = ot;
      for (let i = 0; i < 5; i += 1) {
        if (!cursor) break;
        if (Number(cursor.id) === Number(rootId)) {
          if (cursor.name) map.set(Number(rootId), cursor.name);
          break;
        }
        cursor = cursor.parentType;
      }
    });
    return map;
  }, [lead?.leadOpportunities]);

  // Order the roots: known ones in ROOT_OPP_TYPES order first, then
  // any unknown roots the lead has, ascending by id.
  const orderedRootIds = useMemo(() => {
    const knownIds = ROOT_OPP_TYPES.map(c => c.rootOppTypeId);
    const knownIdSet = new Set(knownIds);
    const known = knownIds.filter(id => presentRoots.has(id));
    const unknown = [...presentRoots]
      .filter(id => !knownIdSet.has(Number(id)))
      .sort((a, b) => a - b);
    return [...known, ...unknown];
  }, [presentRoots]);

  if (orderedRootIds.length === 0) return null;

  return (
    <span className="flex items-center gap-1">
      {orderedRootIds.map(rootId => {
        const cfg = getRootConfig(rootId, rootNameById.get(Number(rootId)));
        const { Icon, stroke, chipBg, displayName } = cfg;
        const isHighlighted = Number(highlightedRootId) === Number(rootId);
        if (isHighlighted) {
          return (
            <Tooltip key={rootId} title={tooltipFormatter(displayName)} placement="top">
              <span
                className="flex items-center justify-center"
                style={{
                  width: chipSize,
                  height: chipSize,
                  background: chipBg,
                  borderRadius: 4,
                  flexShrink: 0,
                }}
              >
                <Icon size={size} color={stroke} strokeWidth={1.33} />
              </span>
            </Tooltip>
          );
        }
        return (
          <Tooltip key={rootId} title={tooltipFormatter(displayName)} placement="top">
            <span className="flex items-center" style={{ flexShrink: 0 }}>
              <Icon size={size} color={stroke} strokeWidth={1.33} />
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
};

export default LeadOppTypeIcons;
